import React, { useState, useCallback, useRef, JSX } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';

interface InfoTooltipProps {
  children: React.ReactNode;
  render: (props: { open: boolean }) => JSX.Element;
  triggerOnHover?: boolean;
}

export default function InfoTooltip({ children, render }: InfoTooltipProps) {
  const [, setReferenceElement] = useState<HTMLElement | null>(null);
  const [, setPopperElement] = useState<HTMLElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const setDelayedOpen = useCallback((open: boolean) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setIsOpen(open);
    }, 200);
  }, []);

  const handleMouseEnter = useCallback(() => setDelayedOpen(true), [setDelayedOpen]);
  const handleMouseLeave = useCallback(() => setDelayedOpen(false), [setDelayedOpen]);

  return (
    <Popover className="flex relative">
      <PopoverButton
        ref={setReferenceElement}
        aria-label="Open tooltip"
        className="focus:border-0 focus:outline-0 w-15 h-15"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={(e) => e.preventDefault()}
      >
        {render({ open: isOpen })}
      </PopoverButton>
      <PopoverPanel
        static={isOpen}
        ref={setPopperElement}
        anchor="top"
        className="w-217 border-1 border-accent-2-500 bg-accent-2-100 p-12 rounded-xs text-body-xs [--anchor-gap:8px] overflow-visible z-100"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
        <div className="size-0 mx-auto grid place-content-center translate-y-12">
          <div className="size-8 bg-accent-2-100 border-1 border-r-accent-2-500 border-b-accent-2-500  rotate-45"></div>
        </div>
      </PopoverPanel>
    </Popover>
  );
}
